import { useForm } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { z } from 'zod/v4'
import { ApiError } from '@/api/client'
import { forgotPassword } from '@/api/resources/auth'
import { FormInput } from '@/components/forms/form-input'
import { Button } from '@/components/ui/button'
import { useServerErrors } from '@/hooks/use-server-errors'

const forgotPasswordSchema = z.object({
  email: z.email('Please enter a valid email address'),
})

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>

export const Route = createFileRoute('/_auth/forgot-password')({
  head: () => ({ meta: [{ title: 'Forgot Password - MyJunea' }] }),
  component: ForgotPasswordPage,
})

function ForgotPasswordPage(): React.ReactElement {
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const mutation = useMutation({
    mutationFn: async (data: ForgotPasswordFormValues) => forgotPassword(data),
    onSuccess: () => {
      clearServerErrors()
    },
    onError: handleMutationError,
  })

  const form = useForm({
    defaultValues: { email: '' } as ForgotPasswordFormValues,
    validators: {
      onSubmit: forgotPasswordSchema,
    },
    onSubmit: ({ value }) => {
      mutation.mutate(value)
    },
  })

  if (mutation.isSuccess) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <h1 className="text-primary text-3xl leading-10 font-extrabold tracking-tight">
            Check your email
          </h1>
          <p className="text-muted-foreground text-sm">
            If an account exists with that email, we have sent a reset link.
          </p>
        </div>
        <Link to="/login" className="text-muted-foreground text-center text-sm underline">
          Back to login
        </Link>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <h1 className="text-primary text-3xl leading-10 font-extrabold tracking-tight">
          Forgot password
        </h1>
        <p className="text-muted-foreground text-sm">
          Enter your email address and we will send you a link to reset your password.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          clearServerErrors()
          mutation.reset()
          void form.handleSubmit()
        }}
        className="flex flex-col gap-6"
      >
        <form.Field name="email">
          {(field) => (
            <FormInput
              field={field}
              label=""
              type="email"
              placeholder="Email address"
              autoComplete="email"
              serverErrors={serverErrors}
            />
          )}
        </form.Field>

        {mutation.error instanceof ApiError && mutation.error.status !== 422 && (
          <p className="text-destructive text-sm">{mutation.error.body.message}</p>
        )}

        <Button
          type="submit"
          className="h-12 w-full rounded-full text-base font-semibold"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Sending...' : 'Send reset link'}
          {!mutation.isPending && <ArrowRight className="ml-1 size-5" />}
        </Button>
      </form>

      <Link to="/login" className="text-muted-foreground text-center text-sm underline">
        Back to login
      </Link>
    </div>
  )
}
